// useAssetModels — server state for the products-catalog surface (ADR-065).
// ONE full fetch (pagination: none — the admin front-page pattern): the catalog
// is instance-bounded curated content and the page filters/sorts client-side.
// The picker's category-narrowed search keeps its own key under the same root,
// so every mutation invalidates both with one call.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateAssetModelRequest, UpdateAssetModelRequest } from '@ems-app/schemas/catalog/requests';
import type { AssetModelListResponse, AssetModelProjectItem, AssetModelSearchItem } from '@ems-app/schemas/catalog/responses';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import { apiClient } from '../lib/apiClient.js';
import type { LookupSource } from './useLookup.js';
import type { FacedRow, ServerSource } from './useOptions.js';
import { refreshOnSettled } from '../lib/settled.js';

/** The products of ONE category as a picker's source (ADR-080 §3 — the server kind over the catalogue's search; the
 *  face is `useProductFace()`): "Used in this project" first (a window of five at rest), the catalogue, the category's
 *  generic row pinned last by the server. The search names no ids — a form that holds a product seeds its row (`known`). */
export function productSource(projectId: string, category: AssetCategory, face: (row: AssetModelSearchItem) => FacedRow, opts: { known?: readonly AssetModelSearchItem[] } = {}): ServerSource<'asset-models'> {
  return { kind: 'server', projectId, lookup: productLookup(category), face, known: opts.known };
}
/** The lookup behind the products of one category — the ONE shape of its key, for the source and for a host that asks
 *  its start answer outside the field (the wizard's generic preselect through `lookupSearchOptions`). */
export function productLookup(category: AssetCategory): LookupSource<'asset-models'> {
  return { entity: 'asset-models', params: { category } };
}

export const assetModelKeys = {
  root: ['asset-models'] as const,
  catalog: (projectId: string) => ['asset-models', 'catalog', projectId] as const,
  detail: (projectId: string, id: string) => ['asset-models', 'detail', projectId, id] as const,
};

export function useAssetModelCatalog(projectId: string) {
  return useQuery({
    queryKey: assetModelKeys.catalog(projectId),
    queryFn: async () => {
      // Front page (pagination: none) — the full set in one read; hidden rows
      // ride along so the "Not offered" exception chip can count + reveal them.
      const r = await apiClient.get<AssetModelListResponse>(`/projects/${projectId}/asset-models`, {
        params: { include_generics: false, include_disabled: true },
      });
      return r.data;
    },
  });
}

/** The product's detail read. `quiet` — a form's Load the latest: a product gone meanwhile answers the form, never
 *  the not-found page (ADR-084 §6). */
export async function fetchAssetModel(projectId: string, modelId: string, quiet = false): Promise<AssetModelProjectItem> {
  return (await apiClient.get<AssetModelProjectItem>(`/projects/${projectId}/asset-models/${modelId}`, quiet ? { quietNotFound: true } : undefined)).data;
}

export function useAssetModel(projectId: string, modelId: string | null) {
  return useQuery({
    queryKey: assetModelKeys.detail(projectId, modelId ?? ''),
    queryFn: () => fetchAssetModel(projectId, modelId as string),
    enabled: modelId !== null,
  });
}

export function useCreateAssetModel(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateAssetModelRequest) => {
      const r = await apiClient.post<{ id: string }>(`/projects/${projectId}/asset-models`, body);
      return r.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: assetModelKeys.root }),
  });
}

export function useUpdateAssetModel(projectId: string, modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateAssetModelRequest) => {
      await apiClient.patch(`/projects/${projectId}/asset-models/${modelId}`, body);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: assetModelKeys.root })),
  });
}
